import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { BoxOfferType } from "@/modules/boxOffer/entities/boxOfferTypes"
import { getOfferTypeIcon, getOfferVisibility } from "@/modules/boxOffer/utils/boxOfferUtils"
import { cn } from "@/modules/lib/utils"
import { dragAndDrop } from "@formkit/drag-and-drop/react"
import { BoxOfferItemEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { useBoxOffers, useUpdateOffersOrder } from "@modules/boxOffer/hooks/useBoxOffer"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { GripVertical } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export function BoxOfferOrderForm() {
  const { data: offers } = useBoxOffers()
  const [visibleOrder, setVisibleOrder] = useState<string[]>([])
  const [hiddenOrder, setHiddenOrder] = useState<string[]>([])
  const { hideSidebar } = useSidebar()
  const { saveOrder, isPending } = useUpdateOffersOrder()
  const visibleListRef = useRef<HTMLDivElement>(null)
  const canEditOffersOrder = useBoxStaffPermission(BoxStaffPermissions.EDIT_OFFER)

  const formatPriceLabel = (offer: BoxOfferItemEntity) => {
    if (!offer.price) return "Free"

    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: offer.price.currency,
    })

    if (offer.type === BoxOfferType.Subscription && offer.minimumCommitmentPeriod) {
      return `${formatter.format(offer.price.value)}${word("global.price.priceSlashPerMonth")}`
    }

    return `${formatter.format(offer.price.value)} ${word("global.price.once")}`
  }

  useEffect(() => {
    const sortByDisplay = (items: BoxOfferItemEntity[]) =>
      [...items].sort((a, b) => {
        const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER
        const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER
        if (ao === bo) return a.name.localeCompare(b.name)
        return ao - bo
      })

    const visibleOffers = sortByDisplay(offers.filter((offer) => offer.showcaseDisplay))
    const hiddenOffers = sortByDisplay(offers.filter((offer) => !offer.showcaseDisplay))

    const nextVisibleIds = visibleOffers.map((offer) => offer.id)
    const nextHiddenIds = hiddenOffers.map((offer) => offer.id)

    /**
     * Avoid unnecessary rerenders - we cannot use useMemo here since we need to update the order with drag and drop.
     * We also dont want to do it only once in case offers are updated which can happen since the side menu does not prevent the user to interact with the rest of the offers page
     */
    const hasVisibleDiff =
      nextVisibleIds.length !== visibleOrder.length || nextVisibleIds.some((id) => !visibleOrder.includes(id))
    const hasHiddenDiff =
      nextHiddenIds.length !== hiddenOrder.length || nextHiddenIds.some((id) => !hiddenOrder.includes(id))

    if (hasVisibleDiff) setVisibleOrder(nextVisibleIds)
    if (hasHiddenDiff) setHiddenOrder(nextHiddenIds)
  }, [offers, visibleOrder, hiddenOrder])

  useEffect(() => {
    if (!visibleListRef.current) return
    if (!canEditOffersOrder) return
    dragAndDrop({
      parent: visibleListRef,
      state: [visibleOrder, setVisibleOrder],
      group: "boxOfferVisible",
    })
  }, [visibleOrder, canEditOffersOrder])

  const handleSaveOrder = async () => {
    if (!canEditOffersOrder) return
    await saveOrder(visibleOrder)
    hideSidebar()
  }

  const renderList = ({ ids, hidden }: { ids: string[]; hidden: boolean }) => {
    if (ids.length === 0) {
      return (
        <div className="flex h-16 items-center justify-center border-t border-neutral-700">
          <Text fontStyle="BS" className="text-neutral-600">
            {word("box.offer.form.order.list.empty")}
          </Text>
        </div>
      )
    }

    return ids.map((id) => {
      const offer = offers.find((o) => o.id === id)
      if (!offer) return null
      const visibilityInfo = hidden
        ? getOfferVisibility(offer, "var(--neutral-400)")
        : getOfferVisibility(offer, "var(--neutral-700)")

      return (
        <div
          key={id}
          data-id={id}
          className={cn(
            "grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-1 border-t border-neutral-700 bg-surface-background px-2 py-4",
            hidden || !canEditOffersOrder ? "cursor-default" : "cursor-grab",
          )}
        >
          <GripVertical
            size={18}
            color="currentColor"
            className={cn("mr-3", hidden || !canEditOffersOrder ? "text-neutral-400" : "text-textDefault-body")}
          />
          <div className="flex flex-col">
            <Text fontStyle="BM" className={cn(hidden ? "text-neutral-400" : "text-textDefault-body")}>
              {offer.name}
            </Text>
            <Text
              fontStyle="BS"
              color="currentColor"
              className={cn("flex items-center gap-1", hidden ? "text-neutral-400" : "text-textDefault-caption")}
            >
              {getOfferTypeIcon(offer.type, "currentColor", 16)}
              {word(`box.offer.form.types.${offer.type}`)}
            </Text>
          </div>

          <div className="flex items-center gap-2 px-3 py-1 text-neutral-300">
            <div
              className={cn(
                "flex items-center",
                hidden ? "h-4 w-4 text-neutral-400" : "h-4.5 w-4.5 text-textDefault-caption",
              )}
            >
              {visibilityInfo.icon}
            </div>
            <Text fontStyle="BS" className={hidden ? "text-neutral-400" : "text-textDefault-caption"}>
              {visibilityInfo.text}
            </Text>
          </div>

          <div className="flex justify-end">
            <Text fontStyle="BM" className={hidden ? "text-neutral-400" : "text-textDefault-caption"}>
              {formatPriceLabel(offer)}
            </Text>
          </div>
        </div>
      )
    })
  }

  const renderSection = (type: "hidden" | "visible") => {
    const { title, subtitle, textColor, list, listRef } = {
      hidden: {
        title: word("box.offer.form.order.section.hidden.title"),
        subtitle: word("box.offer.form.order.section.hidden.description"),
        textColor: "text-neutral-400",
        list: hiddenOrder,
        listRef: null,
      },
      visible: {
        title: word("box.offer.form.order.section.visible.title"),
        subtitle: word("box.offer.form.order.section.visible.description"),
        textColor: "text-textDefault-heading",
        list: visibleOrder,
        listRef: visibleListRef,
      },
    }[type]

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Text fontStyle="TM" className="text-textDefault-caption">
            {title}
          </Text>
          <Text fontStyle="BS" className="text-textDefault-caption">
            {subtitle}
          </Text>
        </div>

        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-1 px-3">
            <div className="mr-3" />
            <Text fontStyle="BM" className={"ml-4" + textColor}>
              {word("box.offer.form.order.section.column.offer")}
            </Text>
            <Text fontStyle="BM" className={"ml-5" + textColor}>
              {word("box.offer.form.order.section.column.visibility")}
            </Text>
            <Text fontStyle="BM" className={"text-right " + textColor}>
              {word("box.offer.form.order.section.column.price")}
            </Text>
          </div>

          <div className="flex flex-col" ref={listRef}>
            {renderList({ ids: list, hidden: type === "hidden" })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-8 pt-16">
      <div className="flex flex-col gap-1">
        <Text fontStyle="TL" className="text-textDefault-heading">
          {word("box.offer.form.order.title")}
        </Text>
        <Text fontStyle="BM" className="text-textDefault-body">
          {word("box.offer.form.order.description")}
        </Text>
      </div>

      {renderSection("visible")}

      {renderSection("hidden")}

      <div className="flex justify-between gap-5 py-3">
        <Button variant="outline" size="lg" className="flex-1" onClick={() => hideSidebar()}>
          {word("global.cancel")}
        </Button>

        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={() => void handleSaveOrder()}
          disabled={!canEditOffersOrder || isPending}
        >
          {word("global.update")}
        </Button>
      </div>
    </div>
  )
}
